
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { Datetime } from 'vue-datetime';
import { TimeZone } from "@/store/types";
import { Getter } from 'vuex-class';
import { TimeConstants } from '@/types/Constants';
export enum ParkType {
    Fixed = "fixed",
    DateTime = "datetime"
}

export interface ParkEventData {
    parkType: ParkType;
    parkHours: number;
    parkMinutes: number;
    parkUntilDateTime: string;
}

@Component({
    components: {
        'datetime': Datetime
    }
})
export default class ParkEventOptions extends Vue {

    @Prop({ type: Number, default: 0 }) private initialHours!: number;
    @Prop({ type: Number, default: 10 }) private initialMinutes!: number;
    @Prop({ type: Object, default: null }) private areaTimeZone!: TimeZone | null;
	@Getter getFeature: (featuresList: string[]) => boolean;
	@Getter("getMaxParkSecondsPrefValue") maxParkSecondsPrefValue: number | null;
	@Getter("getTimeZoneLabel") getTimeZoneLabel: (timeZone: TimeZone) => string;

    private parkType: ParkType = ParkType.Fixed;
    private parkDays: number = 0;
    private parkHours: number = 0;
    private parkMinutes: number = 0;
    private parkUntilDateTime: string = "";

    private maxParkSeconds: number = TimeConstants.SECONDS_PER_DAY;

    private readonly ParkType = ParkType;

    private mounted(): void {
        this.parkHours = this.initialHours;
        this.parkMinutes = this.initialMinutes;

        if (this.isTenantMaxParkTimeValueEnabled && this.maxParkSecondsPrefValue)
        {
            this.maxParkSeconds = this.maxParkSecondsPrefValue;
        }

        if (this.allowMultipleDays)
        {
            this.parkDays = Math.floor(this.parkHours / TimeConstants.HOURS_PER_DAY);
            this.parkHours = Math.floor(this.parkHours % TimeConstants.HOURS_PER_DAY);
            if (this.parkDays >= this.maxWholeParkDays)
            {
                this.parkDays = this.maxWholeParkDays;
            }
        }

        if (this.parkHours >= this.maxWholeParkHours)
        {
            this.parkHours = this.maxWholeParkHours;
        }

        if (this.parkMinutes >= this.maxWholeParkMinutes)
        {
            this.parkMinutes = this.maxWholeParkMinutes;
        }
    }

    @Watch("parkType")
    private onParkTypeChanged(newValue: ParkType): void {
        if (newValue === ParkType.DateTime) {

            this.setUntilDateTime();

            const currentTime = Date.now();
            const hoursOffset = (this.parkDays * TimeConstants.MILLISECONDS_PER_DAY) + (this.parkHours * TimeConstants.MILLISECONDS_PER_HOUR);
            const minutesOffset = this.parkMinutes * TimeConstants.MILLISECONDS_PER_MINUTE;
            const adjustedToMaxTime = currentTime + hoursOffset + minutesOffset;
            const maxDateTime = new Date(adjustedToMaxTime);

            const parkUntilTime = new Date(this.parkUntilDateTime);
            this.$emit('park-date-time-valid', (parkUntilTime.getTime() > currentTime) && (currentTime <= maxDateTime.getTime()));

        } else {
            this.validateDays();
            this.validateHours();
            this.validateMinutes();

            this.$emit('park-date-time-valid', true); // Reset validation state when park type change
        }

        this.emitState();
    }

    @Watch("parkUntilDateTime")
    private onParkUntilDateTimeChanged(value: string): void {
        if (value)
        {
            const dateValue = new Date(value);

            if (dateValue.getSeconds() > 0 || dateValue.getMilliseconds() > 0)
            {
                dateValue.setSeconds(0);
                dateValue.setMilliseconds(0);

                value = dateValue.toISOString();
                this.parkUntilDateTime = value;
                return;
            }

            const currentTime = Date.now();

            if (dateValue.getTime() < currentTime) {
                this.$emit('park-date-time-valid', false);

                return;
            }

            this.$emit('park-date-time-valid', true);

            const diffMinutes = Math.ceil((dateValue.getTime() - currentTime) / TimeConstants.MILLISECONDS_PER_MINUTE);
            const clamped = Math.max(0, diffMinutes);
            this.parkHours = Math.floor(clamped / TimeConstants.MINUTES_PER_HOUR);

            if (this.allowMultipleDays)
            {
                this.parkDays = Math.floor(this.parkHours / TimeConstants.HOURS_PER_DAY);

                if (this.parkDays > this.maxWholeParkDays)
                {
                    this.parkDays = this.maxWholeParkDays;
                }

                this.parkHours = this.parkHours % TimeConstants.HOURS_PER_DAY;
            }

            this.parkMinutes = clamped % 60;

            if (this.allowMultipleDays && this.parkHours >= this.maxWholeParkHours) {
                this.parkDays = Math.floor(this.parkHours / TimeConstants.HOURS_PER_DAY);
                this.parkHours =  this.maxWholeParkHours;
            }
        }
        else
        {
            this.$emit('park-date-time-valid', false);
        }

        this.emitState();
    }

    private setUntilDateTime(): void {
        if (!this.isParkUntilDateTimeEnabled) return;

        const hoursOffset = (this.parkDays * TimeConstants.MILLISECONDS_PER_DAY) + (this.parkHours * TimeConstants.MILLISECONDS_PER_HOUR);
        const minutesOffset = this.parkMinutes * TimeConstants.MILLISECONDS_PER_MINUTE;

        const adjustedTime = new Date(Date.now() + hoursOffset + minutesOffset);
        adjustedTime.setSeconds(0);
        adjustedTime.setMilliseconds(0);

        this.parkUntilDateTime = adjustedTime.toISOString();
    }

    private validateDays(): void {
        if (this.parkDays < 0) {
            this.parkDays = 0;
        } else if (this.parkDays >= this.maxWholeParkDays) {
            this.parkDays = this.maxWholeParkDays;

            if (this.parkHours >= this.maxWholeParkHours) {
                this.parkHours = this.maxWholeParkHours;

                if (this.parkMinutes >= this.maxWholeParkMinutes) {
                    this.parkMinutes = this.maxWholeParkMinutes;
                }
            }
        }

        this.emitState();
    }

    private validateHours(): void {
        if (this.parkHours < 0) {
            this.parkHours = 0;
        } else if (this.parkHours >= this.maxWholeParkHours) {
            this.parkHours = this.maxWholeParkHours;

            if (!this.allowMultipleDays || this.parkDays >= this.maxWholeParkDays) {
                this.parkMinutes = this.maxWholeParkMinutes;
            }
        }

        this.emitState();
    }

    private validateMinutes(): void {
        if (this.parkMinutes < 0) {
            this.parkMinutes = 0;
        } else if (this.parkHours >= this.maxWholeParkHours && this.parkDays >= this.maxWholeParkDays) {
            this.parkMinutes = this.maxWholeParkMinutes;
        } else if (this.parkMinutes > 59) {
            this.parkMinutes = 59;
        }

        this.emitState();
    }

    private getDateString(calculateTimezone: boolean) {
        const dateStr = new Date(this.parkUntilDateTime).toLocaleDateString("en-US", {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                timeZone: calculateTimezone && this.areaTimeZone ? this.areaTimeZone.iana : undefined
            }
        );

        const timeStr = new Date(this.parkUntilDateTime).toLocaleTimeString("en-US", {
            hour12: true,
            hour: "numeric",
            minute: "numeric",
            timeZone: calculateTimezone && this.areaTimeZone ? this.areaTimeZone.iana : undefined
        });
        return `${dateStr} ${timeStr}`;
    }

    private emitState(): void {
        this.$emit('park-options-changed', {
            parkType: this.parkType,
            parkHours: this.parkHours + (this.parkDays * TimeConstants.HOURS_PER_DAY),
            parkMinutes: this.parkMinutes,
            parkUntilDateTime: this.parkUntilDateTime
        });
    }

    private get isParkUntilDateTimeEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Park", "ParkUntilDateTime"]);
	}

	public get isTenantMaxParkTimeValueEnabled(): boolean {
		return this.getFeature(["TenantSettings", "SetMaxParkTime"]);
	}

    private get isParkingAnEventOptional(): boolean {
		return this.getFeature(["Alarms", "EventQueue", "OptionalParkOnLeave"]);
    }

    private get localDateTimeDisplay(): string {
        if (!this.parkUntilDateTime) return "";
        return this.getDateString(false);
    }

    private get areaDateTimeDisplay(): string {
        if (!this.parkUntilDateTime || !this.areaTimeZone?.iana) return "";
        return this.getDateString(true);
    }

    private get minParkDateTime(): string {
        const today = new Date();
        return today.toISOString();
    }

    private get maxParkDateTime(): string {
        const maxParkDateTime = new Date(this.minParkDateTime);
        maxParkDateTime.setTime(maxParkDateTime.getTime() + this.maxParkSeconds * TimeConstants.MILLISECONDS_PER_SECOND);
        return maxParkDateTime.toISOString();
    }

    private get maxWholeParkDays(): number
    {
        if (!this.allowMultipleDays) {
            return 0;
        }

        return Math.floor(this.maxParkSeconds / TimeConstants.SECONDS_PER_DAY);
    }

    private get maxWholeParkHours(): number
    {
        if (this.allowMultipleDays) {
            if (this.parkDays >= this.maxWholeParkDays)
            {
                const remainingHours = Math.floor((this.maxParkSeconds - (this.maxWholeParkDays * TimeConstants.SECONDS_PER_DAY)) / TimeConstants.SECONDS_PER_HOUR);
                if (remainingHours > 0)
                {
                    return remainingHours;
                }

                return 0;
            }

            return TimeConstants.HOURS_PER_DAY - 1;
        } else {
            return TimeConstants.HOURS_PER_DAY;
        }
    }

    private get maxWholeParkMinutes(): number
    {
        if (this.parkHours >= this.maxWholeParkHours && this.parkDays >= this.maxWholeParkDays)
        {
            const remainingMinutes = Math.floor((this.maxParkSeconds - ((this.maxWholeParkDays * TimeConstants.SECONDS_PER_DAY) + (this.maxWholeParkHours * TimeConstants.SECONDS_PER_HOUR))) / TimeConstants.SECONDS_PER_MINUTE);
            if (remainingMinutes > 0)
            {
                return remainingMinutes;
            }

            return 0;
        }

        return TimeConstants.MILLISECONDS_PER_HOUR - 1;
    }

    private get allowMultipleDays(): boolean
    {
        const maxParkSecondsInDays = this.maxParkSeconds / TimeConstants.SECONDS_PER_DAY;
        return maxParkSecondsInDays > 1;
    }
}
